import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getGroupCode, normalizeGroupCode } from '../../../../utils/firebase/groupCodes';
import { formatPreviewCardName, formatPreviewVariation } from '../../../../utils/groups/groupCodePreview';
import InputBar from '../../../../components/input-bar/InputBar';
import PrimaryBtn from '../../../../components/btns/primary-btn/PrimaryBtn';
import CardCountPill from '../../../../components/card-count-pill/CardCountPill';

import {
    GroupCodeSection,
    GroupCodeForm,
    GroupCodeError,
    PreviewPanel,
    PreviewHeader,
    PreviewList,
    PreviewCard,
    PreviewCta,
    PreviewActions
} from './GroupCodeLookup.styles';

const MISS_MESSAGE = 'No cards found for that code.';

export default function GroupCodeLookup() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [code, setCode] = useState('');
    const [isLookingUp, setIsLookingUp] = useState(false);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (!searchParams.has('code')) return;
        setSearchParams({}, { replace: true });
    }, [searchParams, setSearchParams]);

    const lookupCode = useCallback(async (rawCode) => {
        const normalized = normalizeGroupCode(rawCode);
        if (!normalized) {
            setPreview(null);
            setError('');
            return;
        }

        setIsLookingUp(true);
        setError('');

        try {
            const result = await getGroupCode(normalized);
            const cards = result?.cards ?? [];
            if (!result || cards.length === 0) {
                setPreview(null);
                setError(MISS_MESSAGE);
                return;
            }

            setPreview({
                code: result.code,
                title: result.title,
                cardCount: result.cardCount,
                cards: result.cards
            });
        } catch {
            setPreview(null);
            setError(MISS_MESSAGE);
        } finally {
            setIsLookingUp(false);
        }
    }, []);

    const handleSubmit = (event) => {
        event.preventDefault();
        lookupCode(code);
    };

    return (
        <GroupCodeSection>
            <GroupCodeForm onSubmit={handleSubmit}>
                <InputBar
                    id="groupCode"
                    label="Have a code?"
                    placeholder="PV-00000-XXXX"
                    value={code}
                    updateValue={setCode}
                    autoComplete="off"
                    spellCheck={false}
                    align="center"
                />
                    <PrimaryBtn
                        title={isLookingUp ? 'LOOKING UP' : 'VIEW'}
                        onClick={handleSubmit}
                        isLoading={isLookingUp}
                        disabled={!code.trim()}
                        width="fill"         
                    />
            </GroupCodeForm>
            {error && <GroupCodeError>{error}</GroupCodeError>}
            {preview && (
                <PreviewPanel>
                    <PreviewHeader>
                        <h2>{preview.title || 'Your cards'}</h2>
                        <CardCountPill value={preview.cardCount || preview.cards.length || 0} />
                    </PreviewHeader>
                    <PreviewList>
                        {preview.cards.map((card, index) => {
                            const variation = formatPreviewVariation(card.variation);
                            const meta = [
                                card.cardNumber ? `#${card.cardNumber}` : null,
                                card.series,
                                variation
                            ].filter(Boolean).join(' · ');

                            return (
                                <PreviewCard key={`${card.cardNumber || 'card'}-${index}`}>
                                    <strong>{formatPreviewCardName(card)}</strong>
                                    {meta && <span>{meta}</span>}
                                </PreviewCard>
                            );
                        })}
                    </PreviewList>
                    <PreviewCta>
                        Sign in, then enter {preview.code} in the header to add these cards.
                    </PreviewCta>
                    <PreviewActions>
                        <PrimaryBtn
                            title="SIGN IN"
                            onClick={() => navigate('/auth', { state: { tab: 'signIn' } })}
                        />
                        <PrimaryBtn
                            title="CREATE ACCOUNT"
                            onClick={() => navigate('/auth', { state: { tab: 'createAccount' } })}
                        />
                    </PreviewActions>
                </PreviewPanel>
            )}
        </GroupCodeSection>
    );
}
